import type {
  APMAsset,
  Cognite3DModel,
  CogniteCADRevision,
  ICDFView,
  ViewVersions,
} from '@cognite/apm-client';
import { FDMClient } from '@cognite/fdm-client';
import type {
  AnnotationFilterProps,
  AnnotationsAssetRef,
  CogniteClient,
  Model3D,
  Revision3D,
} from '@cognite/sdk';

import type { IThreeDService } from './3d-service-interface';
import { getAssetModelsQuery } from './dms-queries/get-asset-models';
import type { ModelAndRevision3D, ThreeDModelIdentifier } from './types';
import { getModelIdFromExternalId } from './utils';

type CogniteClientInstancesDependencies = Pick<
  CogniteClient['instances'],
  'search'
>;
type CogniteClientViewsDependencies = Pick<CogniteClient['views'], 'retrieve'>;

export type CogniteClientDependencies = Pick<
  CogniteClient,
  'get' | 'post' | 'getBaseUrl' | 'project'
> & {
  instances: CogniteClientInstancesDependencies;
  views: CogniteClientViewsDependencies;
  models3D: Pick<CogniteClient['models3D'], 'list' | 'retrieve'>;
  revisions3D: Pick<
    CogniteClient['revisions3D'],
    'list' | 'retrieve' | 'list3DNodes'
  >;
  assetMappings3D: Pick<CogniteClient['assetMappings3D'], 'filter'>;
  annotations: Pick<CogniteClient['annotations'], 'list' | 'reverseLookup'>;
};

type APMViewVersions = Pick<
  ViewVersions,
  | 'COGNITE_3D_MODEL'
  | 'COGNITE_CAD_REVISION'
  | 'COGNITE_3D_OBJECT'
  | 'COGNITE_CAD_NODE'
>;

export type APMClientDependencies = {
  assets: Pick<ICDFView<APMAsset>, 'viewReference'>;
  viewVersions: APMViewVersions;
};

export class ThreeDService extends FDMClient implements IThreeDService {
  client;
  apmClient;
  constructor(
    client: CogniteClientDependencies,
    apmClient: APMClientDependencies
  ) {
    super(client);
    this.client = client;
    this.apmClient = apmClient;
  }

  async get3DModels(pageParam?: string, pageSize = 50) {
    const { items: threeDModels, nextCursor } = await this.client.models3D.list(
      {
        limit: pageSize,
        cursor: pageParam,
      }
    );
    const pageInfo = {
      hasNextPage: Boolean(nextCursor),
      endCursor: nextCursor,
    };

    const revisionItems: ModelAndRevision3D[][] = await Promise.all(
      threeDModels.map(async (model: Model3D) => {
        const { items } = await this.client.revisions3D.list(model.id);
        return items.map((revision: Revision3D) => {
          return {
            thumbnailURL: revision.thumbnailURL ?? '',
            revisionId: revision.id,
            modelId: model.id,
            name: model.name,
            createdTime: revision.createdTime,
            thumbnailFileId: revision.thumbnailThreedFileId,
          };
        });
      })
    );

    return { models: revisionItems.flat(1), pageInfo };
  }

  /**
   * Fetches detailed model and latest revision information for a given set of 3D model IDs.
   *
   * For each model ID:
   * - Retrieves the model metadata.
   * - Retrieves the list of its 3D revisions.
   * - Selects the most recently created revision (based on `createdTime`).
   * - Returns a structured object containing model and revision details.
   *
   * If either the model retrieval or revision retrieval fails, or if no revisions exist,
   * that model ID will be excluded from the results.
   *
   * @param {number[]} modelIds - An array of 3D model IDs to fetch details for.
   * @returns {Promise<ModelAndRevision3D[]>} A promise that resolves to an array of objects
   * containing combined model and latest revision details. Entries for IDs with errors or
   * no revisions are omitted.
   */
  async fetchModelAndRevisionDetailsByModelIds(
    modelIds: number[]
  ): Promise<ModelAndRevision3D[]> {
    const model3DDetailMappingRequests = modelIds.map(
      async (id): Promise<ModelAndRevision3D | undefined> => {
        const modelRequest = this.client.models3D.retrieve(id);
        const revisionRequest = this.client.revisions3D.list(id);
        const [modelResult, revisionsResult] = await Promise.allSettled([
          modelRequest,
          revisionRequest,
        ]);

        if (
          modelResult.status === 'rejected' ||
          revisionsResult.status === 'rejected'
        ) {
          return undefined;
        }

        const model = modelResult.value;
        const revisions = revisionsResult.value.items;

        if (revisions.length === 0) {
          return undefined;
        }

        const revision = revisions.sort(
          (a, b) => b.createdTime.getTime() - a.createdTime.getTime()
        )[0];

        return {
          thumbnailURL: revision.thumbnailURL ?? '',
          revisionId: revision.id,
          modelId: model.id,
          name: model.name,
          createdTime: revision.createdTime,
          thumbnailFileId: revision.thumbnailThreedFileId,
        };
      }
    );

    const model3DDetailMappings = await Promise.all(
      model3DDetailMappingRequests
    );

    return model3DDetailMappings.filter((mapping) => mapping !== undefined);
  }

  async get3DModelById(modelId: number) {
    return this.client.models3D.retrieve(modelId);
  }

  async getRevisionById(modelId: number, revisionId: number) {
    const { name } = await this.client.models3D.retrieve(modelId);
    const revision = await this.client.revisions3D.retrieve(
      modelId,
      revisionId
    );

    return {
      thumbnailURL: revision.thumbnailURL ?? '',
      revisionId: revision.id,
      modelId,
      name,
      createdTime: revision.createdTime,
      thumbnailFileId: revision.thumbnailThreedFileId,
    } as ModelAndRevision3D;
  }

  async filterAssetMappingsByAssetId(
    modelId: number,
    revisionId: number,
    assetId: number
  ) {
    return this.client.assetMappings3D
      .filter(modelId, revisionId, {
        filter: { assetIds: [assetId] },
      })
      .autoPagingToArray();
  }

  async get3DNodesByNodeId(
    modelId: number,
    revisionId: number,
    nodeId: number
  ) {
    const { items } = await this.client.revisions3D.list3DNodes(
      modelId,
      revisionId,
      {
        nodeId,
        depth: 0,
        limit: 1,
      }
    );

    return items;
  }

  async getAnnotations(annotationFilterProps: AnnotationFilterProps) {
    return this.client.annotations.list({
      filter: annotationFilterProps,
    });
  }

  /**
   * Performs a reverse lookup to find all 3D model annotations linked to a specific asset.
   *
   * @param {number} assetId - The ID of the asset for which to look up associated 3D annotations.
   * @returns {Promise<AnnotationsAssetRef[]>}
   */
  async reverseLookup3dAnnotations(
    assetId: number
  ): Promise<AnnotationsAssetRef[]> {
    return await this.client.annotations
      .reverseLookup({
        filter: {
          annotatedResourceType: 'threedmodel',
          data: {
            assetRef: {
              id: assetId,
            },
          },
        },
      })
      .autoPagingToArray({
        limit: Infinity,
      });
  }

  // Download all 3d nodes
  // that's designed to be used only with lightweight models, where nodes number is around a hundred,
  // in full size models there can be millions of 3d nodes so this one should not be used
  async getAll3DNodesOfAModel(modelId: number, revisionId: number) {
    return this.client.revisions3D
      .list3DNodes(modelId, revisionId)
      .autoPagingToArray({ limit: 1000 });
  }

  // Get all mapped models of a classic asset
  async getClassicAssetModels(assetId: number) {
    return this.client.get<{
      items: ThreeDModelIdentifier[];
    }>(
      `api/v1/projects/${this.client.project}/3d/mappings/${assetId}/modelnodes`
    );
  }

  // Get all mapped models of an asset in IDM
  async getIdmAssetModels(
    assetExternalId: string
  ): Promise<ThreeDModelIdentifier[]> {
    // TODO(FUS-000): Move it to a better place
    const space = 'cdf_cdm';

    const queryData = getAssetModelsQuery(
      assetExternalId,
      this.apmClient.assets.viewReference,
      this.apmClient.viewVersions
    );

    const { threeDModels } = await this.cogniteClient
      .post<{
        items: {
          asset: any[];
          object3D: any[];
          cadNode: any[];
          revisions: any[];
          model3D: any[];
        };
      }>(`${this.BASE_URL}/models/instances/query`, {
        data: queryData,
        withCredentials: true,
      })
      .then(({ data: { items } }) => {
        const models: Cognite3DModel[] = items.model3D?.map((revision) => {
          return {
            ...revision.properties[space][
              `Cognite3DModel/${this.apmClient.viewVersions.COGNITE_3D_MODEL}`
            ],
            externalId: revision.externalId,
            space: revision.space,
          };
        });

        const revisions: CogniteCADRevision[] = items.revisions?.map(
          (revision) => {
            return {
              ...revision.properties[space][
                `CogniteCADRevision/${this.apmClient.viewVersions.COGNITE_CAD_REVISION}`
              ],
              externalId: revision.externalId,
            };
          }
        );

        const threeDModels: ThreeDModelIdentifier[] = revisions.map(
          (revision) => {
            return {
              revisionId: revision.revisionId,
              modelId: getModelIdFromExternalId(revision.model3D.externalId),
              name: models.find(
                (model) =>
                  model.externalId === revision.model3D.externalId &&
                  model.space === revision.model3D.space
              )?.name,
            };
          }
        );

        return { threeDModels };
      });

    return threeDModels;
  }
}
